import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { ProductCard } from '@/components/shop/ProductCard';
import { useCollectionProducts } from '@/hooks/useProducts';
import { Button } from '@/components/ui/button';
import { StructuredData } from '@/components/seo/StructuredData';
import { PageSeoHead } from '@/components/seo/PageSeoHead';

import collectionMountain from '@/assets/collection-mountain.jpg';
import collectionFauna from '@/assets/collection-fauna.jpg';
import collectionEquestrian from '@/assets/collection-equestrian.jpg';

// Map URL slugs to Shopify collection handles and fallback images.
// `prefilter` optionally restricts the products shown to those whose tags
// or title match one of the matchers (used by keyword-targeted SEO landing pages).
const collectionMapping: Record<string, { shopifyHandle: string; image: string; key: string; prefilter?: string[] }> = {
  mountain: { shopifyHandle: 'collezione-montagna', image: collectionMountain, key: 'mountain' },
  fauna: { shopifyHandle: 'collezione-fauna', image: collectionFauna, key: 'fauna' },
  equestrian: { shopifyHandle: 'equitazione', image: collectionEquestrian, key: 'equestrian' },
  // SEO landing pages — reuse existing Shopify collections, override SEO copy
  'gifts-for-hunters': { shopifyHandle: 'collezione-fauna', image: collectionFauna, key: 'giftsHunters' },
  'gifts-for-climbers': { shopifyHandle: 'collezione-montagna', image: collectionMountain, key: 'giftsClimbers' },
  'carabiner-necklaces': { shopifyHandle: 'collezione-montagna', image: collectionMountain, key: 'carabinerNecklaces', prefilter: ['moschettone', 'moschettoni', 'carabiner', 'rinvio', 'rinvii', 'quickdraw'] },
  'equestrian-jewelry': { shopifyHandle: 'equitazione', image: collectionEquestrian, key: 'equestrianJewelry' },
};

// Slugs that show on the cross-collection cards at the bottom (only the 3 main collections)
const MAIN_COLLECTION_SLUGS = ['mountain', 'fauna', 'equestrian'];

// Product type keys and their tag matchers
const productTypeMatchers: Record<string, string[]> = {
  bracelet: ['braccialetto', 'braccialetti', 'bracciale'],
  necklace: ['collana', 'collane'],
  belt: ['cintura', 'cinture'],
  keychain: ['portachiavi'],
  earrings: ['orecchini'],
  cork: ['tappo', 'tappi'],
  pin: ['spilla', 'spille'],
};

const CollectionDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const [selectedType, setSelectedType] = useState<string | null>(null);
  
  const collectionConfig = slug ? collectionMapping[slug] : null;
  const shopifyHandle = collectionConfig?.shopifyHandle || '';
  
  // Fetch collection + products from Shopify
  const { data, isLoading, error } = useCollectionProducts(shopifyHandle);
  
  const collectionTitle = data?.collection?.title || t(`collections.${collectionConfig?.key}.name`);
  const collectionDescription = data?.collection?.description || t(`collections.${collectionConfig?.key}.description`);
  const rawProducts = data?.products;

  // Apply landing-page prefilter (e.g. only carabiner products)
  const products = useMemo(() => {
    if (!rawProducts) return rawProducts;
    const prefilter = collectionConfig?.prefilter;
    if (!prefilter || prefilter.length === 0) return rawProducts;
    return rawProducts.filter(product => {
      const tags = (product.node.tags || []).map((t: string) => t.toLowerCase());
      const title = product.node.title.toLowerCase();
      return prefilter.some(m => tags.includes(m) || title.includes(m));
    });
  }, [rawProducts, collectionConfig]);
  const productTypes = useMemo(() => {
    if (!products) return [];
    const types = new Set<string>();
    products.forEach(product => {
      const tags = (product.node.tags || []).map((t: string) => t.toLowerCase());
      const title = product.node.title.toLowerCase();
      for (const [typeKey, matchers] of Object.entries(productTypeMatchers)) {
        if (matchers.some(m => tags.includes(m) || title.includes(m))) {
          types.add(typeKey);
        }
      }
    });
    return Array.from(types).sort();
  }, [products]);

  // Filter products by selected type
  const filteredProducts = useMemo(() => {
    if (!products || !selectedType) return products;
    const matchers = productTypeMatchers[selectedType] || [];
    return products.filter(product => {
      const tags = (product.node.tags || []).map((t: string) => t.toLowerCase());
      const title = product.node.title.toLowerCase();
      return matchers.some(m => tags.includes(m) || title.includes(m));
    });
  }, [products, selectedType]);

  if (!collectionConfig) {
    return (
      <Layout>
        <section className="py-20 bg-background">
          <div className="container-wide text-center">
            <h1 className="font-heading text-3xl text-foreground mb-4">
              {t('collections.notFound', 'Collection not found')}
            </h1>
            <Link to="/collections">
              <Button variant="outline">
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t('collections.backToCollections', 'Back to Collections')}
              </Button>
            </Link>
          </div>
        </section>
      </Layout>
    );
  }

  const otherCollections = Object.entries(collectionMapping).filter(
    ([key]) => key !== slug && MAIN_COLLECTION_SLUGS.includes(key)
  );
  const isIT = i18n.language === 'it';

  const collectionSeoMeta: Record<string, { titleIT: string; titleEN: string; descIT: string; descEN: string; seoContentIT: string; seoContentEN: string }> = {
    mountain: {
      titleIT: 'Collezione Montagna – Gioielli Alpinismo in Bronzo | Sampaoli',
      titleEN: 'Mountain Collection – Bronze Climbing Jewelry | Sampaoli',
      descIT: 'Gioielli artigianali ispirati all\'alpinismo: piccozze, moschettoni e rinvii in bronzo. Fatti a mano in Italia.',
      descEN: 'Handcrafted climbing-inspired jewelry: ice axes, carabiners and quickdraws in bronze. Made in Italy.',
      seoContentIT: 'La Collezione Montagna di Sampaoli Creazioni nasce dalla passione per l\'alpinismo e l\'arrampicata. Ogni pezzo è una riproduzione fedele in miniatura di attrezzatura da montagna reale — piccozze, moschettoni, rinvii, friends e grigri — realizzata interamente a mano nel nostro laboratorio a Teolo, nel cuore dei Colli Euganei. Il bronzo, materiale nobile e duraturo, viene fuso, rifinito e talvolta impreziosito con placcature in argento, oro o rodio. Questi gioielli montagna in bronzo sono pensati per alpinisti, scalatori, escursionisti e per chiunque porti la montagna nel cuore. Ogni braccialetto, collana e cintura racconta una storia di vette conquistate e avventure condivise. Ipoallergenici e resistenti, sono il regalo perfetto per gli amanti della montagna.',
      seoContentEN: 'The Mountain Collection by Sampaoli Creazioni is born from a passion for mountaineering and rock climbing. Each piece is a faithful miniature reproduction of real climbing gear — ice axes, carabiners, quickdraws, cams, and belay devices — entirely handcrafted in our workshop in Teolo, in the heart of Italy\'s Euganean Hills. Noble and durable bronze is cast, finished, and sometimes enhanced with silver, gold, or rhodium plating. These handcrafted bronze mountain jewelry pieces are designed for climbers, alpinists, hikers, and anyone who carries the mountains in their heart. Every bracelet, necklace, and belt tells a story of summits reached and adventures shared. Hypoallergenic and built to last, they make the perfect gift for mountain lovers.',
    },
    fauna: {
      titleIT: 'Collezione Fauna – Spille e Gioielli Fauna Selvatica | Sampaoli',
      titleEN: 'Wildlife Collection – Bronze Animal Pins & Jewelry | Sampaoli',
      descIT: 'Cervi, stambecchi, camosci e rapaci: gioielli artigianali in bronzo ispirati alla fauna alpina. Fatti a mano in Italia.',
      descEN: 'Deer, ibex, chamois and raptors: handcrafted bronze jewelry inspired by alpine wildlife. Made in Italy.',
      seoContentIT: 'La Collezione Fauna di Sampaoli Creazioni celebra la bellezza della fauna selvatica alpina attraverso gioielli artigianali in bronzo. Cervi, caprioli, stambecchi, camosci, marmotte, aquile, galli cedroni e molte altre specie prendono vita in spille, cinture, portachiavi e braccialetti di straordinario realismo. Ogni dettaglio anatomico — dalle corna ramificate del cervo alle piume del rapace — è modellato con precisione e fuso a mano nel nostro laboratorio nei Colli Euganei. Questi gioielli fauna selvatica in bronzo sono ideali per cacciatori, naturalisti, osservatori di fauna e per chiunque ami la vita all\'aria aperta. Perfetti come regalo per cacciatori e amanti della natura, i nostri pezzi sono ipoallergenici, resistenti e destinati a durare nel tempo.',
      seoContentEN: 'The Wildlife Collection by Sampaoli Creazioni celebrates the beauty of alpine wildlife through handcrafted bronze jewelry. Deer, roe deer, ibex, chamois, marmots, eagles, capercaillies, and many more species come to life in pins, belts, keychains, and bracelets of extraordinary realism. Every anatomical detail — from branching deer antlers to raptor feathers — is precisely modeled and hand-cast in our Euganean Hills workshop. These bronze wildlife jewelry pieces are perfect for hunters, naturalists, wildlife observers, and anyone who loves the great outdoors. Ideal as gifts for hunters and nature lovers, our pieces are hypoallergenic, durable, and built to last a lifetime.',
    },
    equestrian: {
      titleIT: 'Collezione Equestre – Gioielli a Tema Cavallo in Bronzo | Sampaoli',
      titleEN: 'Equestrian Collection – Bronze Horse Jewelry | Sampaoli',
      descIT: 'Ferri di cavallo, staffe, speroni e morsi: gioielli artigianali in bronzo per cavalieri e amanti dei cavalli.',
      descEN: 'Horseshoes, stirrups, spurs and bits: handcrafted bronze jewelry for riders and horse lovers.',
      seoContentIT: 'La Collezione Equestre di Sampaoli Creazioni è dedicata al nobile mondo dell\'equitazione. Ferri di cavallo, staffe, speroni, morsi e stivali diventano eleganti gioielli artigianali in bronzo, ciascuno una riproduzione fedele in miniatura dell\'attrezzatura equestre reale. Dalla tradizione inglese del dressage allo spirito western delle praterie, ogni pezzo cattura l\'essenza del legame tra cavaliere e cavallo. Realizzati a mano nel nostro laboratorio a Teolo, i nostri gioielli equestri in bronzo sono pensati per cavalieri, amazzoni e amanti dei cavalli. Ipoallergenici e arricchiti con placcature pregiate, sono il regalo ideale per chi vive la passione equestre ogni giorno.',
      seoContentEN: 'The Equestrian Collection by Sampaoli Creazioni is dedicated to the noble world of horsemanship. Horseshoes, stirrups, spurs, bits, and boots become elegant handcrafted bronze jewelry, each a faithful miniature reproduction of real equestrian equipment. From English dressage tradition to Western prairie spirit, every piece captures the essence of the bond between rider and horse. Handmade in our Teolo workshop, our bronze equestrian jewelry is designed for riders and horse lovers alike. Hypoallergenic and enhanced with premium platings, they are the ideal gift for anyone who lives the equestrian passion every day.',
    },
    giftsHunters: {
      titleIT: 'Idee Regalo per Cacciatori – Gioielli in Bronzo | Sampaoli',
      titleEN: 'Gifts for Hunters – Handmade Bronze Wildlife Jewelry',
      descIT: 'Regali originali per cacciatori e amanti della fauna: spille, cinture e portachiavi in bronzo fatti a mano in Italia.',
      descEN: 'Original gifts for hunters and wildlife lovers: handmade bronze pins, belts and keychains. Made in Italy.',
      seoContentIT: 'Cercando idee regalo per cacciatori che amano davvero la natura, non c\'è niente di più personale di un pezzo artigianale ispirato alla fauna che inseguono e rispettano. La nostra selezione di regali per cacciatori riunisce spille in bronzo raffiguranti cervi, caprioli, cinghiali, camosci, stambecchi, marmotte e galli cedroni, oltre a cinture, portachiavi e collane modellati a mano nel nostro laboratorio a Teolo. Ogni pezzo è una riproduzione anatomica realistica, fusa nel bronzo con la tecnica della cera persa e rifinita uno per uno. Sono regali durevoli, ipoallergenici e simbolici — pensati per accompagnare il cacciatore in giacca, cintura, zaino o auto. Un regalo per la stagione di caccia, un compleanno, o per celebrare la passione per la vita all\'aperto.',
      seoContentEN: 'Looking for gifts for hunters who actually love the wild — there\'s nothing more personal than a handcrafted piece inspired by the animals they pursue and respect. Our gifts for hunters bring together bronze pins of stags, roe deer, wild boar, chamois, ibex, marmots and capercaillies, alongside belts, keychains and necklaces hand-modeled in our workshop in Teolo, Italy. Every piece is an anatomically realistic reproduction, lost-wax cast in bronze and finished one by one. They\'re durable, hypoallergenic and meaningful — built to live on a hunting jacket, belt, backpack or truck. The right gift for the hunting season, a birthday, or simply to celebrate a passion for the outdoors.',
    },
    giftsClimbers: {
      titleIT: 'Idee Regalo per Climber – Gioielli Arrampicata in Bronzo',
      titleEN: 'Gifts for Climbers – Bronze Carabiner & Climbing Jewelry',
      descIT: 'Regali per climber e alpinisti: moschettoni, rinvii, piccozze e friends in miniatura di bronzo. Fatti a mano in Italia.',
      descEN: 'Gifts for climbers and alpinists: bronze miniature carabiners, quickdraws, ice axes and cams. Handmade in Italy.',
      seoContentIT: 'Trovare il regalo giusto per un climber non è facile: chi arrampica già possiede l\'attrezzatura che gli serve. La nostra collezione di regali per climber risolve il problema trasformando l\'attrezzatura alpinistica in gioielli artigianali. Moschettoni in miniatura, rinvii, piccozze, friends, grigri e nuts — tutti riprodotti fedelmente in bronzo, in scala perfetta per essere indossati come collana, braccialetto o portachiavi. Sono pezzi che parlano subito a chi conosce il gesto dell\'arrampicata. Realizzati a mano nei Colli Euganei, sono regali memorabili per compleanni, lauree, anniversari o per festeggiare una via aperta o una vetta raggiunta.',
      seoContentEN: 'Finding the right gift for a climber is hard: climbers already own the gear they need. Our gifts for climbers solve that by turning climbing gear into handcrafted jewelry. Miniature carabiners, quickdraws, ice axes, cams, belay devices and nuts — each faithfully reproduced in bronze at a wearable scale as a necklace, bracelet or keychain. They\'re pieces that immediately resonate with anyone who knows the motion of climbing. Handmade in Italy\'s Euganean Hills, they make memorable gifts for birthdays, graduations, anniversaries — or to celebrate a hard route or a summit.',
    },
    carabinerNecklaces: {
      titleIT: 'Collana Moschettone in Bronzo Artigianale | Sampaoli',
      titleEN: 'Carabiner Necklace – Handmade Bronze Climbing Jewelry',
      descIT: 'Collane e ciondoli a forma di moschettone e rinvio in bronzo fatti a mano. Per climber, alpinisti e amanti della montagna.',
      descEN: 'Bronze carabiner and quickdraw necklaces, handmade in Italy. For climbers, alpinists and mountain lovers.',
      seoContentIT: 'La nostra collana moschettone è la versione gioiello dell\'oggetto più riconoscibile dell\'arrampicata. Ogni moschettone in bronzo è modellato uno per uno con la tecnica della cera persa, riproducendo fedelmente la forma del moschettone reale — leva, ghiera, profilo asimmetrico — in scala adatta a essere indossata. Disponiamo anche di rinvii in bronzo completi, perfetti per chi vuole un pezzo più articolato. Realizzati nel nostro laboratorio a Teolo, sono ipoallergenici, resistenti e impreziositi con placcature in argento, oro o rodio su richiesta. Una collana moschettone è il regalo ideale per climber, alpinisti e per chiunque viva la montagna come parte della propria identità.',
      seoContentEN: 'Our carabiner necklace is the jewelry version of climbing\'s most recognizable object. Each bronze carabiner is hand-modeled one by one using lost-wax casting, faithfully reproducing the real carabiner — gate, sleeve, asymmetric profile — at a wearable scale. We also offer full bronze quickdraws for a more articulated piece. Made in our Teolo workshop, they\'re hypoallergenic, durable and available with silver, gold or rhodium plating on request. A carabiner necklace is the ideal gift for climbers, alpinists, and anyone for whom the mountains are part of who they are.',
    },
    equestrianJewelry: {
      titleIT: 'Gioielli Equestri – Collane Cavallo in Bronzo | Sampaoli',
      titleEN: 'Equestrian Jewelry – Horse Necklaces & Bronze Riding Gifts',
      descIT: 'Collane cavallo, ferri di cavallo e gioielli equestri in bronzo fatti a mano. Per cavalieri, amazzoni e amanti dei cavalli.',
      descEN: 'Horse necklaces, horseshoes and equestrian jewelry in bronze, handmade in Italy. For riders and horse lovers.',
      seoContentIT: 'I nostri gioielli equestri raccontano il legame tra cavaliere e cavallo attraverso il bronzo. Collane con ciondolo a forma di cavallo, ferri di cavallo, staffe, speroni e morsi: ogni pezzo è una riproduzione fedele in miniatura dell\'attrezzatura e dei simboli equestri reali, modellata e fusa a mano nel nostro laboratorio nei Colli Euganei. Una collana cavallo è il regalo ideale per amazzoni, cavalieri di dressage, fantini, appassionati di equitazione western o per chiunque viva il mondo della scuderia. Il bronzo è ipoallergenico, duraturo e si presta a placcature in argento, oro o rodio.',
      seoContentEN: 'Our equestrian jewelry tells the story of the bond between rider and horse in bronze. Horse necklaces, horseshoes, stirrups, spurs and bits — every piece is a faithful miniature reproduction of real riding gear and equestrian symbols, hand-modeled and cast in our Euganean Hills workshop in Italy. A horse necklace is the ideal gift for riders, dressage and show jumping enthusiasts, Western riders and anyone who lives the stable life. Bronze is hypoallergenic, durable, and available with silver, gold or rhodium plating.',
    },
  };

  const meta = collectionSeoMeta[collectionConfig.key] || collectionSeoMeta.mountain;

  return (
    <Layout>
      <PageSeoHead
        title={isIT ? meta.titleIT : meta.titleEN}
        description={isIT ? meta.descIT : meta.descEN}
        path={`/collections/${slug}`}
      />
      <StructuredData
        type="breadcrumb"
        items={[
          { name: 'Home', url: '/' },
          { name: t('nav.collections'), url: '/collections' },
          { name: collectionTitle, url: `/collections/${slug}` },
        ]}
      />
      <section className="relative h-[40vh] md:h-[50vh] overflow-hidden">
        <img
          src={collectionConfig.image}
          alt={collectionTitle}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/80 via-charcoal/40 to-transparent" />
        <div className="absolute inset-0 flex items-end">
          <div className="container-wide pb-8 md:pb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Link 
                to="/collections" 
                className="inline-flex items-center gap-2 text-cream/80 hover:text-cream text-sm mb-4 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('collections.title')}
              </Link>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-ivory mb-3">
                {collectionTitle}
              </h1>
              <p className="font-body text-cream/90 max-w-2xl">
                {collectionDescription}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-10 md:py-14 bg-background">
        <div className="container mx-auto px-4 max-w-7xl">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-bronze" />
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <p className="text-muted-foreground mb-4">{t('shop.error')}</p>
              <Link to="/collections">
                <Button variant="outline">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {t('collections.backToCollections', 'Back to Collections')}
                </Button>
              </Link>
            </div>
          ) : products && products.length > 0 ? (
            <>
              {/* Filter Bar */}
              {productTypes.length > 1 && (
                <div className="flex flex-wrap gap-2 mb-8">
                <Button
                    variant={selectedType === null ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedType(null)}
                    className="text-sm"
                  >
                    {t('shop.allTypes')}
                  </Button>
                  {productTypes.map((type) => (
                    <Button
                      key={type}
                      variant={selectedType === type ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSelectedType(type)}
                      className="text-sm"
                    >
                      {t(`shop.types.${type}`)}
                    </Button>
                  ))}
                </div>
              )}

              <p className="text-sm text-muted-foreground mb-6">
                {t('shop.showing', { count: filteredProducts?.length || 0 })} {selectedType ? `(${t(`shop.types.${selectedType}`)})` : ''}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {filteredProducts?.map((product, index) => (
                  <ProductCard key={product.node.id} product={product} index={index} />
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-20">
              <p className="text-muted-foreground mb-4">{t('shop.noProducts')}</p>
              <Link to="/shop">
                <Button variant="outline">
                  {t('featured.viewAll')}
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* SEO Content Block */}
      <section className="py-10 md:py-14 bg-background">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="prose prose-sm md:prose-base max-w-none text-muted-foreground leading-relaxed font-body">
            <p>{isIT ? meta.seoContentIT : meta.seoContentEN}</p>
          </div>
        </div>
      </section>

      {/* Cross-Collection Links */}
      <section className="py-12 md:py-16 bg-secondary/50">
        <div className="container mx-auto px-4 max-w-7xl">
          <h2 className="font-display text-2xl md:text-3xl text-foreground text-center mb-8">
            {t('collections.title')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 max-w-3xl mx-auto">
            {otherCollections.map(([key, config]) => (
              <Link
                key={key}
                to={`/collections/${key}`}
                className="group relative overflow-hidden rounded-lg aspect-[2/1] card-hover"
              >
                <img
                  src={config.image}
                  alt={t(`collections.${config.key}.name`)}
                  className="absolute inset-0 w-full h-full object-cover img-zoom"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/80 via-charcoal/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <h3 className="font-heading text-lg font-semibold text-ivory mb-1">
                    {t(`collections.${config.key}.name`)}
                  </h3>
                  <span className="inline-flex items-center gap-1 text-sm text-bronze-light group-hover:gap-2 transition-all">
                    {t('collections.viewCollection')}
                    <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CollectionDetail;
